// --- FULLY CORRECTED FILE: packages/internal-sdk/src/analytics/analyticsClient.ts ---

import { analyticsService } from '@dopomogai/api-client';
import type { User } from '@dopomogai/supabase-client';
import { sessionService } from './sessionService';

type Dict = Record<string, any>;

export type GlobalContext = {
  agent_id?: string;
  version_id?: string;
  is_personalized?: boolean;
  share_id?: string;
  company?: string;
  industry?: string;
};

class AnalyticsClient {
  private global: GlobalContext = {};
  private enabled = true;

  enable(v: boolean) { this.enabled = v; }
  setGlobalContext(ctx: GlobalContext) { this.global = { ...this.global, ...ctx }; }
  getSessionId() { return sessionService.getSessionId(); }
  getVisitorId() { return sessionService.getVisitorId(); }

  /**
   * Identifies a user and merges their anonymous profile.
   * It now constructs the correct payload for the analyticsService.
   */
  identifyUser(user: User) {
    if (!this.enabled || typeof window === 'undefined') return;

    const visitorId = sessionService.getVisitorId();
    if (!visitorId || visitorId === 'server') {
      console.warn('Analytics: Cannot identify user, no valid visitorId found.');
      return;
    }

    // Construct the exact payload the service expects
    const payload = {
      user_id: user.id,
      visitor_id: visitorId,
    };

    // Call the service and log errors, but don't block the app
    analyticsService.identifyUser(payload).catch(err => {
      console.error('Analytics identify-user call failed:', err);
    });
  }

  /**
   * Tracks a user behavior event.
   * It now passes the correct arguments to the analyticsService.
   */
  trackEvent(name: string, payload: Dict = {}, user?: User | null) {
    if (!this.enabled || typeof window === 'undefined') return;
      
    try {
      const doc = typeof document !== 'undefined' ? document : ({} as any);
      const loc = typeof window !== 'undefined' ? window.location : ({} as any);
      
      const visitorId = sessionService.getVisitorId();
      const profileId = user?.id ?? visitorId;
      
      const enrichedProperties = {
        profile_id: profileId,
        user_id: user?.id || undefined,
        visitor_id: visitorId,
        session_id: sessionService.getSessionId(),
        lead_record_id: sessionService.getLeadRecordId() || undefined,
        path: (loc?.pathname || '') + (loc?.search || ''),
        referrer: doc?.referrer || undefined,
        ...this.global,
        ...payload,
      };
      
      // Call the service with the correct arguments: (eventName, properties)
      analyticsService.trackEvent(name, enrichedProperties).catch(err => {
        console.warn(`Analytics event '${name}' failed to track:`, err);
      });
    } catch {
      // swallow errors
    }
  }
}

export const analytics = new AnalyticsClient();